import { createPortal } from "react-dom";
import type { ModelExplainSpec, ExplainMode } from "../lib/modelExplain";

function money(v: number): string {
  const a = Math.abs(v), s = v < 0 ? "-" : "";
  if (a >= 1e9) return `${s}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(0)}K`;
  return `${s}$${a.toFixed(0)}`;
}

// Two small clickable links for a card's meta row — Explainability + Feature importance.
export function ExplainButtons({ onOpen, importanceOnly = false }: {
  onOpen: (m: ExplainMode) => void;
  importanceOnly?: boolean;
}) {
  return (
    <>
      {!importanceOnly && (
        <button type="button" className="explain-btn" onClick={() => onOpen("explain")}>✦ Why this number?</button>
      )}
      <button type="button" className="explain-btn" onClick={() => onOpen("importance")}>✦ What drives the model?</button>
    </>
  );
}

interface BarItem { label: string; value: number }
function DivergingBars({ items, fmt, posLabel, negLabel }: {
  items: BarItem[]; fmt: (v: number) => string; posLabel: string; negLabel: string;
}) {
  const W = 820, padL = 264, padR = 30, top = 30, rowH = 30;
  const innerW = W - padL - padR;
  const chartBottom = top + items.length * rowH;
  const H = chartBottom + 30;
  const maxAbs = Math.max(...items.map((i) => Math.abs(i.value)), 1e-6);
  const xc = padL + innerW / 2;
  const sx = ((innerW / 2) / maxAbs) * 0.88;
  const ticks = [-maxAbs, -maxAbs / 2, 0, maxAbs / 2, maxAbs];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
      {/* direction labels */}
      <text x={padL} y={top - 12} fontSize={11} fontWeight={700} fill="var(--accent-2)">{negLabel}</text>
      <text x={W - padR} y={top - 12} textAnchor="end" fontSize={11} fontWeight={700} fill="var(--accent)">{posLabel}</text>
      {/* gridlines */}
      {ticks.map((v, i) => {
        const x = xc + v * sx;
        return (
          <g key={i}>
            <line x1={x} y1={top} x2={x} y2={chartBottom} stroke="var(--divider)" strokeWidth={v === 0 ? 1.3 : 0.6} />
            <text x={x} y={chartBottom + 16} textAnchor="middle" fontSize={10} fill="var(--t4)" fontFamily="var(--mono)">{fmt(v)}</text>
          </g>
        );
      })}
      {/* bars */}
      {items.map((it, i) => {
        const y = top + i * rowH + rowH / 2;
        const w = Math.max(2, Math.abs(it.value) * sx);
        const x = it.value >= 0 ? xc : xc - w;
        const col = it.value >= 0 ? "var(--accent)" : "var(--accent-2)";
        return (
          <g key={i}>
            <rect x={x} y={y - 9} width={w} height={18} rx={3} fill={col} opacity={0.85} />
            <text x={padL - 12} y={y + 4} textAnchor="end" fontSize={12.5} fill="var(--t1)">{it.label}</text>
            <text x={it.value >= 0 ? x + w + 6 : x - 6} y={y + 4}
              textAnchor={it.value >= 0 ? "start" : "end"} fontSize={11} fill="var(--t3)" fontFamily="var(--mono)">
              {(it.value >= 0 ? "+" : "") + fmt(it.value).replace(/^\+/, "")}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ flex: "1 1 0", minWidth: 80, padding: "10px 12px", borderRadius: 10, background: "var(--surface-2)", textAlign: "center" }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: "var(--t1)", fontFamily: "var(--mono)" }}>{value}</div>
      <div style={{ fontSize: 10.5, color: "var(--t4)", marginTop: 2, textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</div>
    </div>
  );
}

const sectTitle: React.CSSProperties = { fontSize: 13, fontWeight: 800, color: "var(--t1)", margin: "0 0 2px", textTransform: "uppercase", letterSpacing: ".04em" };
const sectSub: React.CSSProperties = { fontSize: 11.5, color: "var(--t4)", margin: "0 0 12px" };

export default function ModelExplainModal({ spec, mode, onClose }: {
  spec: ModelExplainSpec; mode: ExplainMode; onClose: () => void;
}) {
  const isImp = mode === "importance";
  const fmtRaw = (v: number) => (v >= 0 ? "+" : "") + v.toFixed(2);
  const fmtDriver = spec.unit === "money" ? money : (v: number) => (v >= 0 ? "+" : "") + v.toFixed(0);

  return createPortal(
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(8,20,24,.55)", backdropFilter: "blur(3px)",
        display: "grid", placeItems: "center", padding: 20, animation: "xplFade .18s var(--ease-out)" }}>
      <style>{`@keyframes xplFade{from{opacity:0}to{opacity:1}}
        @keyframes xplPop{from{opacity:0;transform:translateY(10px) scale(.98)}to{opacity:1;transform:none}}
        .xpl-card::-webkit-scrollbar{width:9px}
        .xpl-card::-webkit-scrollbar-thumb{background:var(--surface-3);border-radius:8px}`}</style>
      <div className="xpl-card" onClick={(e) => e.stopPropagation()}
        style={{ width: "min(880px, 96vw)", maxHeight: "90vh", overflowY: "auto", background: "var(--surface-elev)",
          borderRadius: "var(--r-card)", boxShadow: "var(--sh-pop)", border: "1px solid var(--hairline)",
          animation: "xplPop .22s var(--ease-out)" }}>

        {/* header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "18px 22px 14px",
          borderBottom: "1px solid var(--divider)", background: "linear-gradient(135deg, rgba(var(--accent-rgb),.10), transparent)" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--accent)", textTransform: "uppercase" }}>
              {isImp ? "What drives the model? · top factors overall" : "Why this number? · how this prediction is built"}
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, color: "var(--t1)", marginTop: 3 }}>{spec.name}</div>
            <div style={{ fontSize: 12, color: "var(--t4)", marginTop: 2 }}>{spec.method}</div>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ border: "1px solid var(--hairline)", background: "var(--surface-2)", color: "var(--t2)", borderRadius: 8,
              width: 32, height: 32, fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: "18px 22px 6px" }}>
          {isImp ? (
            <>
              <h4 style={sectTitle}>Global feature importance</h4>
              <p style={sectSub}>Mean signed SHAP contribution across the portfolio — which inputs move the prediction, and which way.</p>
              <DivergingBars items={spec.importance.map((f) => ({ label: f.feature, value: f.value }))}
                fmt={fmtRaw} posLabel={spec.posLabel} negLabel={spec.negLabel} />
            </>
          ) : (
            <>
              <h4 style={sectTitle}>{spec.predictionLabel}</h4>
              <p style={sectSub}>How one prediction is assembled — every contribution is attributable and reconciles to the model output.</p>
              <DivergingBars items={spec.drivers} fmt={fmtDriver} posLabel={spec.posLabel} negLabel={spec.negLabel} />
            </>
          )}
        </div>

        {/* metrics (explain mode) */}
        {!isImp && (
          <div style={{ padding: "4px 22px 8px" }}>
            <h4 style={sectTitle}>Validation — out-of-sample backtest</h4>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
              {spec.metrics.map((m) => <Metric key={m.label} label={m.label} value={m.value} />)}
            </div>
          </div>
        )}

        {/* narrative */}
        <div style={{ margin: "10px 22px 22px", padding: "12px 14px", borderRadius: 10, background: "var(--surface-2)",
          borderLeft: "3px solid var(--accent)", fontSize: 12.5, lineHeight: 1.5, color: "var(--t2)" }}>
          {isImp ? spec.narrativeImportance : spec.narrativeExplain}
        </div>
      </div>
    </div>,
    document.body,
  );
}
